import { useRef } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useDialogA11y } from '../shells/useDialogA11y';
import type { Density, Theme } from '../theme';

const ROLE_LABEL: Record<string, string> = {
  system_administrator: 'System Administrator',
  compliance_manager: 'Compliance Manager',
  vendor: 'Vendor',
};

/**
 * Settings dialog (Story 1.6, UX-DR8/UX-DR10). Toggles theme + density (applied
 * instantly + persisted per user by the parent) and shows read-only account
 * details. Overlay keyboard contract: Esc closes; focus the dialog on open
 * (UX-DR15).
 */
export function SettingsPanel({
  theme,
  density,
  onToggleTheme,
  onToggleDensity,
  onClose,
}: {
  theme: Theme;
  density: Density;
  onToggleTheme: () => void;
  onToggleDensity: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const { user } = useAuth();
  const dialogRef = useRef<HTMLElement>(null);
  // Esc closes; focus the dialog on open (UX-DR15) and restore focus to the
  // opener on close; Tab is trapped within the dialog (Story 4.4).
  useDialogA11y(dialogRef, onClose);

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--scrim)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 'var(--z-modal)' as unknown as number,
        padding: 'var(--gutter)',
      }}
    >
      <section
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-heading"
        onClick={(e) => e.stopPropagation()}
        className="card stack"
        style={{ width: 'min(460px, 100%)', boxShadow: 'var(--elevation-modal)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 id="settings-heading" style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
            Settings
          </h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close settings">
            Close
          </button>
        </div>

        <div className="stack" style={{ gap: 6 }}>
          <h3 className="muted" style={{ margin: 0, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Account
          </h3>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', margin: 0 }}>
            <dt className="muted">Email</dt>
            <dd style={{ margin: 0 }} data-numeric>
              {user?.email}
            </dd>
            <dt className="muted">Role</dt>
            <dd style={{ margin: 0 }}>{user ? (ROLE_LABEL[user.role] ?? user.role) : ''}</dd>
            {user?.companyId && (
              <>
                <dt className="muted">Company</dt>
                <dd style={{ margin: 0 }} data-numeric>
                  {user.companyId}
                </dd>
              </>
            )}
            {user?.vendorId && (
              <>
                <dt className="muted">Vendor</dt>
                <dd style={{ margin: 0 }} data-numeric>
                  {user.vendorId}
                </dd>
              </>
            )}
          </dl>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span style={{ fontWeight: 600 }}>Theme</span>
          <button
            type="button"
            className="icon-button"
            onClick={onToggleTheme}
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
          >
            {theme === 'light' ? 'Light' : 'Dark'}
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span style={{ fontWeight: 600 }}>Density</span>
          <button
            type="button"
            className="icon-button"
            onClick={onToggleDensity}
            aria-label={`Switch to ${density === 'comfortable' ? 'compact' : 'comfortable'} density`}
          >
            {density === 'comfortable' ? 'Comfortable' : 'Compact'}
          </button>
        </div>
      </section>
    </div>
  );
}
